/* ===========================================================================
   Lazy Preload (global) v2
   ---------------------------------------------------------------------------
   Goal: by the time you can see an image, it's already decoded. No dark
   placeholder, no "loading..." flash anywhere on the site.

   Two parallel mechanisms — whichever fires first wins, both eventually
   converge on "every lazy media is eager":

     1) Idle-time bulk swap. Once the page is idle (or 2s elapsed,
        whichever comes first), sweep every <img loading="lazy"> in the
        DOM and flip it to eager. Browser fetches them in priority order
        via HTTP/2 multiplexing; on Shopify CDN this is fast and parallel.
        After this fires, the bulk of the page is preloaded regardless of
        whether the user has scrolled.

     2) IntersectionObserver, rootMargin "3000px 9999px 3000px 9999px".
        Catches anything added after init (carousel children, theme-editor
        reloads, AJAX content). The huge horizontal margin is critical:
        cards in a horizontal <scroll-carousel> are positioned off-screen
        to the right (e.g. x = 78vw, 156vw...). A vertical-only rootMargin
        misses them entirely until the user actually swipes. 9999px on the
        horizontal axis ensures every card in the carousel intersects as
        soon as the section approaches viewport vertically.

   Videos: <video preload="metadata"|"none"> get bumped to preload="auto"
   plus an explicit .load() to kick off network fetching of the first
   frames. Same observer / same idle sweep.

   Bandwidth opt-out: Save-Data and 2G/slow-2G effective connection types
   skip the entire script and keep native lazy loading. Bandwidth-conscious
   users are not punished.
   =========================================================================== */
(function () {
  if (!('IntersectionObserver' in window)) return;

  var conn = navigator.connection || navigator.webkitConnection || navigator.mozConnection;
  if (conn && (conn.saveData || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g')) return;

  var SELECTOR = 'img[loading="lazy"], video[preload="metadata"], video[preload="none"], iframe[loading="lazy"]';
  var ROOT_MARGIN = '3000px 9999px 3000px 9999px';

  function eagerize(el) {
    var tag = el.tagName;
    if (tag === 'IMG' || tag === 'IFRAME') {
      if (el.loading === 'lazy') el.loading = 'eager';
    } else if (tag === 'VIDEO') {
      if (el.preload !== 'auto') {
        el.preload = 'auto';
        try { el.load(); } catch (err) {}
      }
    }
  }

  var io = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (!e.isIntersecting) continue;
      eagerize(e.target);
      io.unobserve(e.target);
    }
  }, { rootMargin: ROOT_MARGIN });

  function observe(el) {
    if (el.__lazyPreload) return;
    el.__lazyPreload = true;
    io.observe(el);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    var nodes = root.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) observe(nodes[i]);
  }

  /* Idle-time bulk swap — the workhorse. After 2s idle (or 2s timeout),
     promote every remaining lazy media regardless of viewport position.
     By the time the user reaches any section, its media is already in
     flight or in cache. */
  function bulkSwap() {
    var nodes = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) eagerize(nodes[i]);
  }

  function scheduleBulkSwap() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(bulkSwap, { timeout: 2000 });
    } else {
      setTimeout(bulkSwap, 1500);
    }
  }

  function init() {
    scan(document);

    if ('MutationObserver' in window) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.matches && n.matches(SELECTOR)) observe(n);
            scan(n);
          }
        }
      }).observe(document.body || document.documentElement, { childList: true, subtree: true });
    }

    scheduleBulkSwap();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
